import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Cpu, House, HouseSimple, Users, MagnifyingGlass, SignOut, UserCircle, ShieldCheck, HardDrives, ListChecks, CalendarBlank, Bell, Headset, File, Wrench, Ticket, Briefcase, List, X, Sun, Moon } from "@phosphor-icons/react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const nav = [
  { to: "/", label: "Dashboard", icon: House, end: true },
  { to: "/interventi-laboratorio", label: "Interventi laboratorio", icon: Wrench },
  { to: "/domicilio", label: "Interventi a domicilio", icon: HouseSimple },
  { to: "/ticket", label: "Ticket assistenza", icon: Ticket, badgeKey: "tickets" },
  { to: "/calendario", label: "Calendario", icon: CalendarBlank },
  { to: "/attivita?tab=tasks", label: "Task", icon: ListChecks, activeTab: "tasks" },
  { to: "/attivita?tab=works", label: "Lavori", icon: Briefcase, activeTab: "works" },
  { to: "/scadenzario", label: "Scadenzario", icon: Bell },
  { to: "/assistenze", label: "Assistenze", icon: Headset },
  { to: "/clienti", label: "Clienti", icon: Users },
  { to: "/moduli", label: "Moduli", icon: File },
];

const bottomNav = [
  { to: "/profilo", label: "Profilo", icon: UserCircle },
];

const adminNav = [
  { to: "/utenti", label: "Utenti", icon: ShieldCheck, adminOnly: true },
  { to: "/backup", label: "Backup", icon: HardDrives, adminOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [newTicketCount, setNewTicketCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/tickets/count");
        if (!cancelled) setNewTicketCount(data.new || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 45000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Auto-close mobile drawer when navigating
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const items = [...nav, ...(user?.role === "admin" ? adminNav : []), ...bottomNav];

  const badgeFor = (item) => {
    if (item.badgeKey === "tickets" && newTicketCount > 0) return newTicketCount;
    return null;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* MOBILE top bar with hamburger — hidden on lg+ */}
      <header className="lg:hidden sticky top-0 z-40 bg-card border-b border-border flex items-center justify-between px-3 h-14 no-print">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
          className="relative h-10 w-10 grid place-items-center rounded-md hover:bg-secondary"
          aria-label="Apri menu"
          style={{ transition: "background-color 0.15s ease" }}
        >
          <List size={22} weight="bold" />
          {newTicketCount > 0 && (
            <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono font-bold">
              {newTicketCount}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2">
          <img
            src={theme === "dark" ? "/logo-diotech-light.webp" : "/logo-diotech-dark.png"}
            alt="Diotech"
            className="h-8 w-8 object-contain"
          />
          <div className="font-display font-bold tracking-tight text-sm leading-none">DIOTECH</div>
        </div>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle-mobile"
          aria-label={theme === "dark" ? "Passa a tema chiaro" : "Passa a tema scuro"}
          className="h-10 w-10 grid place-items-center rounded-md hover:bg-secondary text-foreground/80 hover:text-foreground"
          style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
        >
          {theme === "dark" ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
        </button>
      </header>

      {/* MOBILE drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40 no-print"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* SIDEBAR — mobile drawer + desktop static */}
      <aside
        className={`no-print z-50
          lg:w-64 lg:h-screen lg:sticky lg:top-0 lg:flex-shrink-0
          bg-card border-r border-border flex flex-col
          fixed inset-y-0 left-0 w-72 max-w-[85vw]
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:relative lg:translate-x-0`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-4 border-b border-border/60 flex items-center gap-3 lg:min-h-[72px] shrink-0">
          <img
            src={theme === "dark" ? "/logo-diotech-light.webp" : "/logo-diotech-dark.png"}
            alt="Diotech"
            className="h-10 w-10 object-contain"
          />
          <div className="flex-1">
            <div className="font-display font-bold tracking-tight text-base leading-none">DIOTECH</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Gestionale IT</div>
          </div>
          {/* Close button — mobile only */}
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-menu-close"
            className="lg:hidden h-8 w-8 grid place-items-center rounded-md hover:bg-secondary"
            aria-label="Chiudi menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 flex flex-col gap-1 px-3 py-4 overflow-y-auto min-h-0">
          {items.map((n) => {
            const badge = badgeFor(n);
            // Custom active detection for query-param tab items (e.g. /attivita?tab=tasks)
            let isActiveOverride = undefined;
            if (n.activeTab) {
              const currentTab = new URLSearchParams(location.search).get("tab") || "works";
              isActiveOverride = location.pathname === "/attivita" && currentTab === n.activeTab;
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s|\//g, "-")}`}
                className={({ isActive }) => {
                  const active = isActiveOverride !== undefined ? isActiveOverride : (isActive && (!n.activeTab));
                  return `flex items-center gap-3 px-3 py-2 rounded-md text-sm whitespace-nowrap
                   ${active ? "bg-primary/10 text-primary font-medium" : "text-foreground/80 hover:bg-secondary hover:text-foreground"}`;
                }}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}
              >
                <n.icon size={18} weight="regular" />
                <span className="flex-1">{n.label}</span>
                {badge != null && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-mono font-bold"
                    data-testid={`nav-badge-${n.label.toLowerCase().replace(/\s|\//g, "-")}`}
                  >{badge}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-border/60 shrink-0 bg-card">
          <div className="px-3 mb-2">
            <div className="font-mono text-[10px] uppercase text-muted-foreground">Operatore</div>
            <div className="text-sm font-medium truncate">{user?.name || "—"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            {user?.role === "admin" && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary">
                <ShieldCheck size={10} /> Admin
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="w-full justify-start gap-2 text-sm mb-1"
            aria-label={theme === "dark" ? "Passa a tema chiaro" : "Passa a tema scuro"}
          >
            {theme === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
            {theme === "dark" ? "Tema chiaro" : "Tema scuro"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-2 text-sm"
          >
            <SignOut size={16} />
            Esci
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
